/**
 * Payment provider: transferencia bancaria manual + comprobante.
 *
 * Hoy es el único método activo. Mantiene el comportamiento del flujo actual
 * de `Expensas.confirmarPago`: el residente sube un comprobante por edificio,
 * los payments quedan en `pendiente_verificacion` esperando que el admin los
 * apruebe en `CobranzaAdmin`.
 *
 * Para "pagar todo junto" cross-edificio: agrupamos por `buildingid` porque
 * cada edificio tiene su propia cuenta bancaria. Una transferencia física
 * solo puede ir a una cuenta, así que un carrito multi-edificio se descompone
 * en N grupos (uno por edificio) y N comprobantes.
 */
import {
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";

const PROVIDER_ID = "manual_transfer";

/**
 * Agrupa payments por edificio. Cada grupo apunta a la cuenta bancaria del
 * edificio (configurada en `building.bankAccounts`).
 *
 * @param {Array} payments — Array de docs payments con `buildingid`, `apartmentId`, etc.
 * @param {Object} ctx
 * @param {Array} ctx.buildings — Array de docs building visibles al residente
 * @returns {Array<{ groupId, buildingId, buildingName, bankAccounts, payments, totalAmount }>}
 */
function groupPayments(payments, { buildings = [] } = {}) {
  const buildingById = new Map();
  buildings.forEach((b) => buildingById.set(String(b.buildingid), b));

  const groups = new Map();
  for (const p of payments) {
    const key = String(p.buildingid);
    if (!groups.has(key)) {
      const building = buildingById.get(key) || null;
      groups.set(key, {
        groupId: `bld_${key}`,
        buildingId: p.buildingid,
        buildingName: building?.name || `Edificio ${key}`,
        bankAccounts: building?.bankAccounts || [],
        payments: [],
        totalAmount: 0,
      });
    }
    const g = groups.get(key);
    g.payments.push(p);
    const remaining = Number(p.remainingAmount);
    const amount = !isNaN(remaining) && remaining > 0
      ? remaining
      : Number(p.amount) || 0;
    g.totalAmount += amount;
  }
  return Array.from(groups.values());
}

/**
 * Sube el comprobante a Storage y marca los N payments del grupo como
 * `pendiente_verificacion`. Comparten `cartGroupId` (UUID) y `comprobanteUrl`
 * para que el admin pueda revisarlos juntos.
 *
 * @param {Object} args
 * @param {Object} args.group — Uno de los grupos retornados por groupPayments
 * @param {File} args.proofFile — Archivo del comprobante (jpg/png/pdf)
 * @param {Object} args.user — Auth user actual
 * @param {string} [args.cartGroupId] — UUID compartido entre payments del grupo
 * @param {number} [args.totalPaidOverride] — opcional, si el residente declaró un monto distinto al sugerido
 * @returns {Promise<{ success: boolean, comprobanteUrl: string, updated: number }>}
 */
async function submitProof({ group, proofFile, user, cartGroupId, totalPaidOverride }) {
  if (!group || !group.payments || group.payments.length === 0) {
    throw new Error("Grupo sin payments para procesar.");
  }
  if (!user) throw new Error("Usuario no autenticado.");

  // 1) Subir comprobante a Storage. Único por grupo.
  let comprobanteUrl = null;
  if (proofFile) {
    const ts = Date.now();
    const safeName = (proofFile.name || "comprobante").replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `comprobantes/${group.buildingId}/${cartGroupId || ts}_${safeName}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, proofFile);
    comprobanteUrl = await getDownloadURL(fileRef);
  }

  // 2) Distribuir el monto pagado entre los N payments del grupo. Si no hay
  // override, asumimos que se pagó el remainingAmount total. Si hay override,
  // lo distribuimos proporcional al monto pendiente de cada payment.
  const sumRemaining = group.payments.reduce((s, p) => {
    const r = Number(p.remainingAmount);
    return s + (!isNaN(r) && r > 0 ? r : Number(p.amount) || 0);
  }, 0);
  const totalPaid =
    typeof totalPaidOverride === "number" && totalPaidOverride > 0
      ? totalPaidOverride
      : sumRemaining;

  const batch = writeBatch(db);
  let updated = 0;
  for (const p of group.payments) {
    const remaining = Number(p.remainingAmount);
    const due = !isNaN(remaining) && remaining > 0
      ? remaining
      : Number(p.amount) || 0;
    if (due <= 0) continue;
    // R4: distribución proporcional SIN truncar. Si el residente pagó de más,
    // el share supera al due y newRemaining queda negativo (crédito a favor, se
    // arrastra al mes siguiente). Si pagó de menos, newRemaining queda positivo.
    const paidNow = sumRemaining > 0 ? (due / sumRemaining) * totalPaid : 0;
    const newPaidAmount = (Number(p.paidAmount) || 0) + paidNow;
    const newRemaining = Math.round((due - paidNow) * 100) / 100;
    batch.update(doc(db, "payments", p.id), {
      status: "pendiente_verificacion",
      paidAmount: Number(newPaidAmount.toFixed(2)),
      remainingAmount: Number(newRemaining.toFixed(2)),
      lastPaymentAttempt: Number(paidNow.toFixed(2)),
      paymentMethod: PROVIDER_ID,
      paymentDate: serverTimestamp(),
      paidBy: user.uid || null,
      paidByEmail: user.email || null,
      comprobanteUrl: comprobanteUrl || p.comprobanteUrl || null,
      cartGroupId: cartGroupId || null,
    });
    updated += 1;
  }
  await batch.commit();

  return { success: true, comprobanteUrl, updated };
}

const manualTransfer = {
  id: PROVIDER_ID,
  label: "Transferencia bancaria",
  description:
    "Hacé una transferencia por cada edificio a la cuenta indicada y subí los comprobantes.",
  needsProofPerGroup: true,
  groupPayments,
  submitProof,
};

export default manualTransfer;
